/**
 * Hierarchical query-key factory. Pure functions returning `as const` tuples,
 * so broad invalidation falls out of prefix matching:
 *
 *   queryClient.removeQueries({ queryKey: ['identity'] })        // every scope
 *   queryClient.invalidateQueries({ queryKey: queryKeys.identity(scope) })
 *
 * Everything the API answers differently per (tenant, user, role) lives under
 * `['identity', scope, ...]`. `scope` is passed in explicitly rather than read
 * from the identity store so these stay pure and directly unit-testable.
 */
export const queryKeys = {
  /** The whole cache partition for one identity. */
  identity: (scope: string) => ['identity', scope] as const,

  myCourses: (scope: string) => ['identity', scope, 'me', 'courses'] as const,
  /**
   * The greyed-out surface (#13). A SIBLING of `myCourses`, not a superset:
   * the same URL answers differently per membership because the lock reasons
   * are derived against the acting tenant, so it has to partition with the
   * rest of `['identity', scope, …]`.
   */
  myCatalog: (scope: string) => ['identity', scope, 'me', 'catalog'] as const,
  tenantUsers: (scope: string) => ['identity', scope, 'tenant', 'users'] as const,
  /**
   * The acting institution's brand — colour, mark, default direction (#210).
   *
   * A SIBLING of `tenantUsers`, never a child: `['identity', scope, 'tenant',
   * 'users']` is the roster's own key, and a branding entry beneath it would be
   * swept in by any invalidation of the roster. One key, one answer.
   *
   * Identity-scoped like everything else, and here that is what makes the paint
   * safe. `useBrandSync` is handed this query's data alongside the acting
   * tenant's id and TRUSTS that the two belong together (see its doc comment in
   * `@cusp/theme`); the scope in the key is the whole of that guarantee, and
   * `installIdentityCacheReset` cancels the in-flight read still carrying the
   * old headers when the scope changes. A shared entry would put one school's
   * colour around another school's data.
   */
  tenantBranding: (scope: string) => ['identity', scope, 'tenant', 'branding'] as const,

  /**
   * The learner course surface (#20). Identity-scoped like everything else,
   * and for a sharper reason than convenience: the catalog has NO row-level
   * security, so what these URLs answer is decided ENTIRELY by the entitlement
   * check the handler runs against the acting membership. The same URL is a
   * lecture body for a seated learner and a 403 for the same human acting in
   * another institution, which makes a shared cache entry a paywall bypass in
   * the client rather than merely a stale read.
   *
   * Keyed on STABLE identity throughout — course slug, version REF (the alias
   * the client asked for, `enrolled`/`current`, not the integer it resolved
   * to), lecture key, component key. A key carrying `lectures.id` would detach
   * from its own subject the next time the course is published.
   */
  course: (scope: string, slug: string) => ['identity', scope, 'course', slug] as const,
  courseVersion: (scope: string, slug: string, versionRef: string) =>
    ['identity', scope, 'course', slug, 'version', versionRef] as const,
  /**
   * The syllabus drawer, opened from the course overview AND from inside the
   * player. A SIBLING of `courseVersion` under the same version ref, so both
   * surfaces share one cache entry — opening the drawer in the player after
   * reading it on the overview is a cache hit, and a version switch drops both
   * together.
   */
  courseSyllabus: (scope: string, slug: string, versionRef: string) =>
    ['identity', scope, 'course', slug, 'version', versionRef, 'syllabus'] as const,
  lecture: (scope: string, slug: string, versionRef: string, lectureKey: string) =>
    ['identity', scope, 'course', slug, 'version', versionRef, 'lecture', lectureKey] as const,
  lectureComponent: (
    scope: string,
    slug: string,
    versionRef: string,
    lectureKey: string,
    componentKey: string,
  ) =>
    [
      'identity',
      scope,
      'course',
      slug,
      'version',
      versionRef,
      'lecture',
      lectureKey,
      'component',
      componentKey,
    ] as const,
  /**
   * Question bank, pearls and tutor thread hang off the LECTURE, not off a
   * version: all three key to stable lecture identity and float above course
   * versions (their routes carry no `versionRef` at all). Nesting them under a
   * version would invent a partition the server does not have.
   */
  lectureQuestions: (scope: string, slug: string, lectureKey: string) =>
    ['identity', scope, 'course', slug, 'lecture', lectureKey, 'questions'] as const,
  lecturePearls: (scope: string, slug: string, lectureKey: string) =>
    ['identity', scope, 'course', slug, 'lecture', lectureKey, 'pearls'] as const,
  /**
   * A lecture's accreditation mapping (#15). Lecture-grain like the bank and
   * the pearls, and for the same reason: mappings key to stable lecture
   * identity and float above course versions — the route carries no version at
   * all, and the server positions the lecture from the caller's enrollment.
   */
  lectureStandards: (scope: string, slug: string, lectureKey: string) =>
    ['identity', scope, 'course', slug, 'lecture', lectureKey, 'standards'] as const,
  tutorTurns: (scope: string, slug: string, lectureKey: string) =>
    ['identity', scope, 'course', slug, 'lecture', lectureKey, 'tutor'] as const,
  /**
   * CuspTutor sessions (#274/#275). A COURSE-grain sibling of the lecture
   * thread, not a child of it: the route is `/courses/:slug/tutor/sessions`
   * and one of its three kinds — `course_discussion` — has no lecture at all,
   * so hanging the list under a lecture would invent a partition the server
   * does not have. The filter object rides IN the key because two filters are
   * two different answers to the same URL; react-query hashes it
   * deterministically.
   */
  tutorSessions: (
    scope: string,
    slug: string,
    filter: { kind?: string; lectureKey?: string } = {},
  ) => ['identity', scope, 'course', slug, 'tutor', 'sessions', filter] as const,
  /** One session with its transcript — a CHILD of the list, so a new session drops both. */
  tutorSession: (scope: string, slug: string, sessionId: string) =>
    ['identity', scope, 'course', slug, 'tutor', 'sessions', 'id', sessionId] as const,
  /**
   * The caller's disclosure acknowledgement (rule 57). Identity-scoped and
   * COURSE-FREE, because the acknowledgement is: it is recorded once per
   * membership per copy revision, and the route carries no course. A key
   * naming one would let a learner meet the first-run card again on their
   * second course.
   */
  tutorDisclosure: (scope: string) => ['identity', scope, 'me', 'tutor', 'disclosure'] as const,
  /**
   * The instructor note on the learner's home page (#107). A SIBLING of
   * `myCourses`, not a child of a course: the route takes no course at all —
   * the server picks the newest current note across every course the caller has
   * one on — so a key naming one would invent a partition the API does not
   * have. Identity-scoped like everything else here, and for the sharpest
   * reason on this list: the same human acting in two institutions must never
   * be served one school's correspondence under the other's name.
   */
  myNote: (scope: string) => ['identity', scope, 'me', 'note'] as const,
  myEnrollments: (scope: string) => ['identity', scope, 'me', 'enrollments'] as const,
  /**
   * One enrollment's full per-lecture progress (#104's continue-hero). A CHILD
   * of `myEnrollments` so a tenant switch drops it with the rest of the
   * partition, and keyed on the enrollment id the summary list already carries.
   */
  enrollment: (scope: string, enrollmentId: string) =>
    ['identity', scope, 'me', 'enrollments', enrollmentId] as const,

  /**
   * The administrator surface. Every key below sits under `['identity', scope,
   * 'admin']`, so one `invalidateQueries` on that prefix refreshes the whole
   * console — and a tenant switch drops all of it, which matters more here than
   * anywhere else in the app: these are cohort-wide reads about named students,
   * and a stale one surviving a switch would show one institution's roster
   * under another's name.
   *
   * The report keys carry their PARAMETERS. Every admin report is addressed to
   * one course over one window, so two courses are two different answers to the
   * same URL; a key that dropped the params would serve PM1's completion curve
   * for BS1 the moment the selector moved. react-query hashes the object
   * deterministically, so the shape is a stable cache identity.
   */
  admin: (scope: string) => ['identity', scope, 'admin'] as const,
  adminCourses: (scope: string) => ['identity', scope, 'admin', 'courses'] as const,
  adminLicenses: (scope: string) => ['identity', scope, 'admin', 'licenses'] as const,
  /**
   * ONE licence and its `utilization` — a SIBLING of `adminLicenses`, for the
   * same reason `adminSeats` is one: `['identity', scope, 'admin', 'licenses']`
   * is the LIST's own key, and a detail entry underneath it would be swept in
   * by any invalidation of the list. Singular `'license'` against plural
   * `'licenses'` is what keeps the two prefixes from matching each other.
   *
   * The list route cannot answer what this one is read for. `seatsInvited` —
   * seats spoken for by codes that have been minted and not yet spent — lives
   * on `LicenseUtilization` and nowhere else, so a screen that wants to say
   * how many seats are free to hand out has to ask per licence.
   */
  adminLicense: (scope: string, licenseId: string) =>
    ['identity', scope, 'admin', 'license', licenseId] as const,
  /**
   * The invite codes minted against one licence. Licence id in the key and the
   * page params after it, exactly as `adminSeats` carries its own: two licences
   * are two different answers to one route, and so are two pages of one.
   */
  adminInvites: (scope: string, licenseId: string, params: object = {}) =>
    ['identity', scope, 'admin', 'invites', licenseId, params] as const,
  /**
   * THE TWO PREFIXES WITHOUT A LICENCE ID, because a mutation does not have
   * one to give.
   *
   * `useMintInvite` and `useRevokeInvite` are told a licence id and a code id
   * respectively, and neither is enough to rebuild the key of every list and
   * detail entry the write moved — the invite list's key carries its page
   * params as well, and a revoke does not even know which licence its code
   * belongs to. These name the whole family instead, which is what
   * `invalidateInviteWrite` needs in order to say "is either of these reads
   * still in flight?" (see its comment in `hooks/admin.ts`).
   *
   * They are the same arrays `adminInvites` and `adminLicense` are built on,
   * written once so the two cannot drift apart.
   */
  adminInvitesAll: (scope: string) => ['identity', scope, 'admin', 'invites'] as const,
  adminLicenseAll: (scope: string) => ['identity', scope, 'admin', 'license'] as const,
  /**
   * One licence's seat rows — a SIBLING of `adminLicenses`, not a child of it,
   * even though the URL nests (`/admin/licenses/:id/seats`).
   * `['identity', scope, 'admin', 'licenses']` is the licence LIST's own key,
   * so a seats key underneath it would make every seats entry match a prefix
   * that means "the list of licences", and `invalidateQueries` on the list
   * would silently sweep in a dozen seat pages. One key, one answer.
   *
   * The licence id is in the key, and the params bag after it for the reason
   * every report key carries its params: two licences are two different answers
   * to one route, and so are the claimed and the free half of one licence.
   */
  adminSeats: (scope: string, licenseId: string, params: object = {}) =>
    ['identity', scope, 'admin', 'seats', licenseId, params] as const,
  adminMembers: (scope: string, params: object = {}) =>
    ['identity', scope, 'admin', 'members', params] as const,
  adminAudit: (scope: string, params: object = {}) =>
    ['identity', scope, 'admin', 'audit', params] as const,
  /**
   * Instructor notes (#107) — course-addressed like every report beside it, and
   * carrying its `courseSlug` for the same reason they carry theirs: two
   * courses are two different answers to one URL, and a key that dropped the
   * parameter would serve PM1's correspondence on the BS1 screen.
   */
  adminNotes: (scope: string, params: object) =>
    ['identity', scope, 'admin', 'notes', params] as const,
  /**
   * The institution rollup (#144) — the one admin report with no course in its
   * key, because it has none in its URL. `windowDays` still travels: two
   * windows are two different answers to one path.
   */
  adminOverview: (scope: string, params: object = {}) =>
    ['identity', scope, 'admin', 'reports', 'overview', params] as const,
  adminEngagement: (scope: string, params: object) =>
    ['identity', scope, 'admin', 'reports', 'engagement', params] as const,
  adminCourseWeeklyActive: (scope: string, params: object) =>
    ['identity', scope, 'admin', 'reports', 'engagement', 'weekly', params] as const,
  /**
   * The same URL with no course named — the institution-wide series. A
   * SEPARATE key rather than the per-course one with `courseSlug: undefined`,
   * because the two answer different shapes: one cache entry that could hold
   * either would hand a component a report with no `course` on it depending on
   * which screen ran first.
   */
  adminInstitutionWeeklyActive: (scope: string, params: object = {}) =>
    ['identity', scope, 'admin', 'reports', 'engagement', 'weekly', 'institution', params] as const,
  /** Latest discovery and concrete editions must not share cached responses. */
  adminSyllabus: (scope: string, courseSlug: string, version?: number) =>
    ['identity', scope, 'admin', 'courses', courseSlug, 'syllabus', version ?? 'latest'] as const,
  adminCourseMaterials: (scope: string, courseSlug: string, version?: number) =>
    ['identity', scope, 'admin', 'courses', courseSlug, 'materials', version ?? 'latest'] as const,
  adminLectureMaterials: (
    scope: string,
    courseSlug: string,
    lectureKey: string,
    version?: number,
  ) =>
    ['identity', scope, 'admin', 'courses', courseSlug, 'materials', version, lectureKey] as const,
  adminLearnerEngagement: (scope: string, params: object) =>
    ['identity', scope, 'admin', 'reports', 'learners', params] as const,
  adminAtRisk: (scope: string, params: object) =>
    ['identity', scope, 'admin', 'reports', 'at-risk', params] as const,
  adminCohort: (scope: string, params: object) =>
    ['identity', scope, 'admin', 'reports', 'cohort', params] as const,
  adminStandardsCoverage: (scope: string, params: object) =>
    ['identity', scope, 'admin', 'reports', 'standards-coverage', params] as const,
  adminContentInsights: (scope: string, params: object) =>
    ['identity', scope, 'admin', 'insights', 'content', params] as const,

  /** Identity-INDEPENDENT — the dev directory is unauthenticated by definition. */
  devIdentities: () => ['dev', 'identities'] as const,
  health: () => ['health'] as const,
};
